<script setup lang="ts">
import { storagePlace } from '../storage-location';
import { ask, askText, dialogState } from '../dialogs';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { CanvasMetaExt } from '@vp/core';
import { useApp } from '../stores/app';
import { locale, t, tn } from '../i18n';
import { reportStorageError, storage } from '../platform';
import { exportCanvasArchive, exportCanvases } from '../archive';
import { pinCanvases, prunePins, readPins, sortCanvases, unpinCanvases, writePins } from '../canvas-pins';
import { ensureThumbnails, loadThumbnail, onThumbnail } from '../editor/thumbnails';
import { INK_FILTER } from '@vp/render';
import { canvasDark, ACCENTS } from '../theme';
import Icon from '../components/Icon.vue';
import HomeAddButton from '../components/HomeAddButton.vue';
import MenuButton from '../components/MenuButton.vue';
import { readHomeLayout, type HomeLayout } from '../home-layout';
import { ui } from '../editor/bridge';

/** "My canvases" (§6.5): light layout with previews, search and backup reminders (§10.6). */
const app = useApp();
const query = ref('');

/** Tiles or a compact list. Chosen in Settings → Appearance. */
const layout = ref<HomeLayout>(readHomeLayout());

const menuFor = ref<string | null>(null);
const menuEl = ref<HTMLElement | null>(null);
const menuPos = ref({ x: 0, y: 0 });
const menuReady = ref(false);
const quota = ref<{ usage?: number; quota?: number }>({});
const persisted = ref<boolean | undefined>(undefined);
const lastExport = ref<number | null>(null);
const thumbs = ref<Record<string, string | null>>({});

/** Pin order is remembered in the profile. Missing canvases are dropped. */
const pins = ref<string[]>(readPins());
/** Selection mode: a click marks a card instead of opening it. */
const picking = ref(false);
const picked = ref(new Set<string>());
const busy = ref(false);
let anchor: string | null = null;

const ordered = computed(() => sortCanvases(app.canvases, pins.value));
const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  return q ? ordered.value.filter((c) => c.name.toLowerCase().includes(q)) : ordered.value;
});
const pickedIds = computed(() => {
  const have = new Set(app.canvases.map((c) => c.id));
  return [...picked.value].filter((id) => have.has(id));
});
const pinSet = computed(() => new Set(pins.value));
const canPin = computed(() => pickedIds.value.some((id) => !pinSet.value.has(id)));
const canUnpin = computed(() => pickedIds.value.some((id) => pinSet.value.has(id)));
const allVisiblePicked = computed(() => filtered.value.length > 0 && filtered.value.every((c) => picked.value.has(c.id)));
const usagePct = computed(() => (quota.value.usage && quota.value.quota ? Math.round((quota.value.usage / quota.value.quota) * 100) : 0));
const exportDays = computed(() => (lastExport.value ? Math.floor((Date.now() - lastExport.value) / 86400000) : null));
const needsBackup = computed(() => app.canvases.length > 0 && (exportDays.value === null || exportDays.value > 7));

/** Old preview URLs are released once the images have switched to the new ones. */
function revokeLater(urls: (string | null | undefined)[]): void {
  setTimeout(() => {
    for (const u of urls) if (u) URL.revokeObjectURL(u);
  }, 1500);
}

async function loadThumbs(): Promise<void> {
  const next: Record<string, string | null> = {};
  for (const c of app.canvases) next[c.id] = await loadThumbnail(c.id);
  const old = Object.values(thumbs.value);
  thumbs.value = next;
  revokeLater(old);
  // missing or outdated previews (imported, duplicated, edited elsewhere) are redrawn in the background
  void ensureThumbnails(app.canvases, app.sessionHooks(), new Set(app.sessions.keys()));
}

/** Solid color covers: the canvas preview is used when none is set. */
const COVERS = ['#e2e8f0', '#fde68a', '#fecaca', '#fbcfe8', '#ddd6fe', '#bfdbfe', '#a5f3fc', '#bbf7d0', ...ACCENTS.slice(0, 6)];
function coverOf(c: CanvasMetaExt): string | null {
  const v = c.settings?.['cover'];
  return typeof v === 'string' ? v : null;
}
async function setCover(c: CanvasMetaExt, color: string | null): Promise<void> {
  await app.setCanvasCover(c.id, color);
}

function onHomeKey(e: KeyboardEvent): void {
  if (dialogState.current || !picking.value) return;
  if (e.key === 'Escape') {
    if (menuFor.value) menuFor.value = null;
    else picking.value = false;
    return;
  }
  const typing = e.target instanceof HTMLElement && e.target.closest('input, textarea') !== null;
  if (!typing && (e.ctrlKey || e.metaKey) && e.code === 'KeyA') {
    e.preventDefault();
    selectVisible();
  }
}

onMounted(async () => {
  window.addEventListener('keydown', onHomeKey);
  await app.refreshCanvases();
  quota.value = await storage.estimate();
  // canvases in a folder do not depend on the browser keeping its storage
  persisted.value = storagePlace.kind === 'folder' ? undefined : await navigator.storage?.persisted?.();
  lastExport.value = (await storage.getSetting<number>('lastExportAt')) ?? null;
});
function syncPins(): void {
  // the list starts empty and is filled after the first read; pruning before that would forget every pin
  if (!app.canvasesLoaded) return;
  const existing = new Set(app.canvases.map((c) => c.id));
  const next = prunePins(pins.value, existing);
  if (next !== pins.value) {
    pins.value = next;
    writePins(next);
  }
  if ([...picked.value].some((id) => !existing.has(id))) {
    picked.value = new Set([...picked.value].filter((id) => existing.has(id)));
  }
}
watch(() => app.canvases, () => {
  void loadThumbs();
  syncPins();
}, { immediate: true });
watch(() => app.canvasesLoaded, syncPins);
watch(picking, (on) => {
  if (on) return;
  picked.value = new Set();
  anchor = null;
});
const offThumb = onThumbnail(async (id) => {
  const old = thumbs.value[id];
  thumbs.value = { ...thumbs.value, [id]: await loadThumbnail(id) };
  revokeLater([old]);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onHomeKey);
  window.removeEventListener('pointerdown', onMenuOutside, true);
  offThumb();
  for (const url of Object.values(thumbs.value)) if (url) URL.revokeObjectURL(url);
});

function when(ts: number): string {
  const d = new Date(ts);
  const loc = locale.value === 'ru' ? 'ru-RU' : 'en-US';
  const sameDay = new Date().toDateString() === d.toDateString();
  return sameDay ? t('home.today', { time: d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' }) }) : d.toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric' });
}

function count(n: number): string {
  return tn('home.objects', n);
}

function placeMenu(anchor: DOMRect): void {
  const menu = menuEl.value;
  if (!menu) return;
  const pad = 8;
  const gap = 6;
  const { width: w, height: h } = menu.getBoundingClientRect();
  const x = Math.min(Math.max(pad, anchor.right - w), Math.max(pad, window.innerWidth - w - pad));
  const below = anchor.bottom + gap;
  const above = anchor.top - gap - h;
  let y: number;
  if (below + h <= window.innerHeight - pad) y = below;
  else if (above >= pad) y = above;
  else {
    const roomBelow = window.innerHeight - pad - below;
    const roomAbove = anchor.top - gap - pad;
    y = roomAbove > roomBelow ? Math.max(pad, above) : Math.min(below, window.innerHeight - h - pad);
  }
  menuPos.value = { x, y };
}

function openMenu(e: MouseEvent, c: CanvasMetaExt): void {
  const anchor = (e.currentTarget as HTMLElement).getBoundingClientRect();
  menuReady.value = false;
  menuFor.value = c.id;
  void nextTick(() => {
    placeMenu(anchor);
    menuReady.value = true;
  });
}

/** A press outside the menu closes it, including on the chrome around the page. */
function onMenuOutside(e: PointerEvent): void {
  const t = e.target as Element | null;
  if (t?.closest?.('.menu, .more, .logo-wrap')) return;
  menuFor.value = null;
}
watch(menuFor, (id) => {
  window.removeEventListener('pointerdown', onMenuOutside, true);
  if (id) window.addEventListener('pointerdown', onMenuOutside, true);
});

function onCardDown(e: MouseEvent): void {
  if (picking.value && e.shiftKey) e.preventDefault();
}

function onCard(e: MouseEvent, c: CanvasMetaExt): void {
  if (!picking.value) {
    void app.openCanvas(c.id);
    return;
  }
  const next = new Set(picked.value);
  const ids = filtered.value.map((x) => x.id);
  if (e.shiftKey && anchor && ids.includes(anchor) && ids.includes(c.id)) {
    const a = ids.indexOf(anchor);
    const b = ids.indexOf(c.id);
    const [lo, hi] = a < b ? [a, b] : [b, a];
    for (let i = lo; i <= hi; i++) next.add(ids[i]);
  } else if (next.has(c.id)) next.delete(c.id);
  else next.add(c.id);
  anchor = c.id;
  picked.value = next;
}

function selectVisible(): void {
  const next = new Set(picked.value);
  for (const c of filtered.value) next.add(c.id);
  picked.value = next;
}

function clearPicked(): void {
  picked.value = new Set();
  anchor = null;
}

function storePins(next: string[]): void {
  pins.value = next;
  writePins(next);
}

function startPick(id: string): void {
  menuFor.value = null;
  picking.value = true;
  const next = new Set(picked.value);
  next.add(id);
  anchor = id;
  picked.value = next;
}

function togglePin(id: string): void {
  menuFor.value = null;
  storePins(pins.value.includes(id) ? unpinCanvases(pins.value, [id]) : pinCanvases(pins.value, [id]));
}

function pinSelected(): void {
  const order = ordered.value.map((c) => c.id).filter((id) => picked.value.has(id));
  storePins(pinCanvases(pins.value, order));
}

function unpinSelected(): void {
  storePins(unpinCanvases(pins.value, pickedIds.value));
}

async function duplicateSelected(): Promise<void> {
  if (busy.value || !pickedIds.value.length) return;
  const ids = ordered.value.map((c) => c.id).filter((id) => picked.value.has(id));
  busy.value = true;
  try {
    for (const id of ids) await app.duplicateCanvas(id);
  } catch (e) {
    reportStorageError(e);
  } finally {
    busy.value = false;
  }
}

async function exportSelected(): Promise<void> {
  if (busy.value || !pickedIds.value.length) return;
  const ids = ordered.value.map((c) => c.id).filter((id) => picked.value.has(id));
  busy.value = true;
  try {
    await exportCanvases(ids);
    lastExport.value = Date.now();
  } catch (e) {
    reportStorageError(e);
  } finally {
    busy.value = false;
  }
}

async function removeSelected(): Promise<void> {
  const ids = ordered.value.map((c) => c.id).filter((id) => picked.value.has(id));
  if (busy.value || !ids.length) return;
  if (!(await ask(tn('home.confirmDeleteMany', ids.length), { title: t('home.delete'), okLabel: t('home.delete'), danger: true }))) return;
  busy.value = true;
  try {
    for (const id of ids) await app.deleteCanvas(id);
    clearPicked();
  } catch (e) {
    reportStorageError(e);
  } finally {
    busy.value = false;
  }
}

async function rename(c: CanvasMetaExt): Promise<void> {
  menuFor.value = null;
  const name = await askText(t('canvas.namePrompt'), c.name, { title: t('home.rename') });
  if (name && name.trim()) await app.renameCanvas(c.id, name.trim());
}

async function duplicate(c: CanvasMetaExt): Promise<void> {
  menuFor.value = null;
  await app.duplicateCanvas(c.id);
}

async function remove(c: CanvasMetaExt): Promise<void> {
  menuFor.value = null;
  if (await ask(t('home.confirmDelete', { name: c.name }), { title: t('home.delete'), okLabel: t('home.delete'), danger: true })) await app.deleteCanvas(c.id);
}

async function exportOne(c: CanvasMetaExt): Promise<void> {
  menuFor.value = null;
  await exportCanvasArchive(c.id);
  lastExport.value = Date.now();
}

/** Backups, import and the storage overview live in Settings → Storage. */
function openStorage(): void {
  app.settingsSection = 'storage';
  app.go('settings');
}
</script>

<template>
  <div class="home" @click="menuFor = null">
    <main class="page">
      <div class="page-inner">
        <div class="head">
          <div class="lead">
            <MenuButton />
          </div>
          <h1 class="sr-only">{{ t('app.myCanvases') }}</h1>
          <div class="actions">
            <label class="search">
              <Icon name="search" :size="15" />
              <input v-model="query" type="search" :placeholder="t('home.search')" :aria-label="t('home.search')" />
            </label>
            <HomeAddButton />
            <button type="button" class="feed-launch" :class="{ active: ui.chatOpen }" :aria-pressed="ui.chatOpen" :aria-expanded="ui.chatOpen" :aria-label="t('feed.open')" :title="t('feed.open')" data-act="feed-home" @click.stop="ui.chatOpen = !ui.chatOpen"><Icon name="menu" :size="16" /></button>
          </div>
        </div>

        <div v-if="needsBackup || usagePct >= 80 || persisted === false" class="notices">
          <button v-if="needsBackup" class="notice warn" :title="t('home.backupHint')" @click="openStorage">
            <Icon name="download" :size="14" />
            {{ exportDays === null ? t('home.lastExportNever') : t('home.lastExport', { days: exportDays }) }} — {{ t('home.backupAction') }}
          </button>
          <button v-if="usagePct >= 80" class="notice warn" @click="openStorage"><Icon name="warning" :size="14" />{{ t('home.storageWarn', { pct: usagePct }) }}</button>
          <span v-if="persisted === false" class="notice" :title="t('home.persistDenied')"><Icon name="info" :size="14" />{{ t('home.persistShort') }}</span>
        </div>

        <div v-if="app.canvasesLoaded && !app.canvases.length" class="empty">
          <div class="empty-art" aria-hidden="true"><Icon name="grid" :size="32" /></div>
          <p>{{ t('home.empty') }}</p>
          <HomeAddButton />
        </div>

        <ul class="cards" :class="{ list: layout === 'list', picking }">
          <li v-for="c in filtered" :key="c.id" class="card" :class="{ picked: picked.has(c.id) }">
            <span v-if="picking" class="pick" :class="{ on: picked.has(c.id) }" aria-hidden="true">
              <Icon v-if="picked.has(c.id)" name="check" :size="13" />
            </span>
            <button class="card-main" :aria-label="c.name" :aria-pressed="picking ? picked.has(c.id) : undefined" @mousedown="onCardDown" @click="onCard($event, c)">
              <div class="thumb" :class="{ cover: !!coverOf(c) }" :style="coverOf(c) ? { background: coverOf(c)! } : undefined">
                <template v-if="!coverOf(c)">
                  <img v-if="thumbs[c.id]" :src="thumbs[c.id]!" alt="" :style="{ filter: canvasDark ? INK_FILTER : undefined }" />
                  <Icon v-else name="grid" :size="26" />
                </template>
              </div>
              <div class="meta">
                <strong class="name-line">
                  <Icon v-if="pinSet.has(c.id)" name="pin" :size="13" class="pin-mark" :title="t('home.pinned')" />
                  <span class="title">{{ c.name }}</span>
                </strong>
                <span class="sub">
                  <span class="count">{{ count(c.objectCount) }}</span>
                  <span class="dot" aria-hidden="true">·</span>
                  <span class="when">{{ when(c.updatedAt) }}</span>
                </span>
              </div>
            </button>
            <button class="more" :aria-label="t('app.menu') + ': ' + c.name" @click.stop="openMenu($event, c)"><Icon :name="layout === 'list' ? 'dotsVertical' : 'dots'" :size="16" /></button>
          </li>
        </ul>

      </div>
    </main>
    <div v-if="menuFor" ref="menuEl" class="menu glass" :class="{ ready: menuReady }" :style="{ left: menuPos.x + 'px', top: menuPos.y + 'px' }" role="menu" @click.stop>
      <template v-for="c in app.canvases.filter((x) => x.id === menuFor)" :key="c.id">
        <button role="menuitem" @click="(menuFor = null), app.openCanvas(c.id)">{{ t('home.open') }}</button>
        <button role="menuitem" @click="startPick(c.id)">{{ t('home.pick') }}</button>
        <button role="menuitem" @click="togglePin(c.id)">{{ pinSet.has(c.id) ? t('home.unpin') : t('home.pin') }}</button>
        <button role="menuitem" @click="rename(c)">{{ t('home.rename') }}</button>
        <button role="menuitem" @click="duplicate(c)">{{ t('home.duplicate') }}</button>
        <button role="menuitem" @click="exportOne(c)">{{ t('home.export') }}</button>
        <hr />
        <div class="cover-block">
          <p class="menu-sec">{{ t('home.cover') }}</p>
          <div class="covers" role="radiogroup" :aria-label="t('home.cover')">
            <button role="radio" class="swatch-btn preview" :class="{ active: !coverOf(c) }" :aria-checked="!coverOf(c)" :title="t('home.coverPreview')" :aria-label="t('home.coverPreview')" @click="setCover(c, null)"><Icon name="image" :size="12" /></button>
            <button v-for="col in COVERS" :key="col" role="radio" class="swatch-btn" :class="{ active: coverOf(c) === col }" :aria-checked="coverOf(c) === col" :style="{ background: col }" :aria-label="col" @click="setCover(c, col)"></button>
            <label class="swatch-btn custom" :class="{ active: !!coverOf(c) && !COVERS.includes(coverOf(c)!) }" :title="t('export.color')">
              <input type="color" :value="coverOf(c) ?? '#bfdbfe'" :aria-label="t('export.color')" @change="setCover(c, ($event.target as HTMLInputElement).value)" />
            </label>
          </div>
        </div>
        <hr />
        <button role="menuitem" class="danger" @click="remove(c)">{{ t('home.delete') }}</button>
      </template>
    </div>
    <div v-if="picking" class="bulk float pillbar" role="toolbar" :aria-label="t('home.bulk')">
      <span class="name static">{{ tn('home.picked', pickedIds.length) }}</span>
      <span class="sep" aria-hidden="true"></span>
      <button type="button" class="chip" :disabled="busy || !filtered.length || allVisiblePicked" @click="selectVisible">{{ t('home.pickAll') }}</button>
      <button type="button" class="chip" :disabled="busy || !pickedIds.length" @click="clearPicked">{{ t('home.pickNone') }}</button>
      <span class="sep" aria-hidden="true"></span>
      <button type="button" class="chip" :disabled="busy || !canPin" @click="pinSelected">{{ t('home.pin') }}</button>
      <button type="button" class="chip" :disabled="busy || !canUnpin" @click="unpinSelected">{{ t('home.unpin') }}</button>
      <button type="button" class="chip" :disabled="busy || !pickedIds.length" @click="duplicateSelected">{{ t('home.duplicate') }}</button>
      <button type="button" class="chip" :disabled="busy || !pickedIds.length" @click="exportSelected">{{ t('home.export') }}</button>
      <button type="button" class="chip danger" :disabled="busy || !pickedIds.length" @click="removeSelected">{{ t('home.delete') }}</button>
      <span class="sep" aria-hidden="true"></span>
      <button type="button" class="chip" :disabled="busy" :aria-label="t('home.pickDone')" :title="t('home.pickDone')" @click="picking = false"><Icon name="close" :size="14" /></button>
    </div>
  </div>
</template>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg);
}
.page {
  flex: 1;
  overflow: auto;
}
/* the list takes the whole width of the window: more columns on a wide screen, bigger previews */
.page-inner {
  max-width: none;
  margin: 0;
  padding: 16px clamp(16px, 2.6vw, 44px) 48px;
}
.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.lead {
  display: flex;
  align-items: center;
  gap: 10px;
}
.actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.pill.tall {
  height: 36px;
  padding: 0 16px;
  font-size: 13px;
}
.pill.primary {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--on-accent);
}
.pill.primary:hover {
  background: var(--accent-2);
  color: var(--on-accent);
}
.feed-launch {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--panel);
  color: var(--text-2);
  box-shadow: var(--shadow);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  cursor: pointer;
}
.feed-launch:hover,
.feed-launch.active {
  color: var(--text);
}
.feed-launch.active {
  box-shadow: var(--shadow), 0 0 0 2px var(--accent);
}
.search {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  color: var(--text-3);
}
.search:focus-within {
  border-color: var(--border-strong);
}
.search input {
  border: none;
  outline: none;
  background: none;
  width: 190px;
  font-size: 13px;
  color: var(--text);
}
.notices {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}
.notice {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 30px;
  padding: 0 12px;
  border-radius: var(--radius-control);
  background: var(--panel);
  border: 1px solid var(--border);
  color: var(--text-3);
  font-size: 12px;
}
.notice.warn {
  background: var(--warn-soft);
  border-color: color-mix(in srgb, var(--warn) 35%, transparent);
  color: var(--warn);
}
button.notice {
  cursor: pointer;
}
button.notice:hover {
  border-color: color-mix(in srgb, var(--warn) 55%, transparent);
}
.cards {
  list-style: none;
  padding: 0;
  margin: 24px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 270px), 1fr));
  gap: clamp(12px, 1.4vw, 22px);
}
.card {
  position: relative;
  background: var(--panel);
  border-radius: var(--radius-panel);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 0 0 1px rgba(15, 23, 42, 0.05);
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-radius 500ms cubic-bezier(0.22, 1, 0.36, 1);
}
.card:hover {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1), 0 0 0 1px rgba(15, 23, 42, 0.06);
  transform: translateY(-1px);
}
.card.picked {
  box-shadow: 0 0 0 2px var(--accent);
}
.card.picked:hover {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.1), 0 0 0 2px var(--accent);
}
.cards.list .card.picked,
.cards.list .card.picked:hover {
  box-shadow: 0 0 0 2px var(--accent);
}
.pick {
  position: absolute;
  z-index: 2;
  top: 14px;
  left: 14px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1.5px solid var(--border-strong);
  background: var(--float);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--on-accent);
  pointer-events: none;
}
.pick.on {
  background: var(--accent);
  border-color: var(--accent);
}
.name-line {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}
.pin-mark {
  flex: none;
  color: var(--accent-text);
}
.name-line .title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.card-main {
  display: block;
  width: 100%;
  border: none;
  background: none;
  padding: 8px;
  text-align: left;
  cursor: pointer;
  border-radius: var(--radius-panel);
}
.thumb {
  /* the preview grows with its card, keeping the proportions of a screen */
  aspect-ratio: 16 / 10;
  border-radius: var(--radius-panel);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border-strong);
  background:
    radial-gradient(circle, var(--border) 1px, transparent 1.2px) 0 0 / 14px 14px,
    var(--panel-2);
}
.thumb.cover {
  background-image: none;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 8px 6px;
}
.meta strong {
  font-weight: 600;
  font-size: 13.5px;
  min-width: 0;
}
.meta .sub {
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 12px;
  color: var(--text-3);
}
.meta .count,
.meta .when {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.meta .dot {
  margin: 0 6px;
  flex: none;
}
.cards.list {
  grid-template-columns: 1fr;
  gap: 10px;
}
.cards.list .pick {
  position: relative;
  top: auto;
  left: auto;
  margin-left: 10px;
  align-self: center;
  flex: none;
}
.cards.list .card {
  display: flex;
  align-items: stretch;
  border-radius: var(--radius-panel);
  box-shadow: 0 0 0 1px var(--border);
  min-width: 0;
  overflow: hidden;
}
.cards.list .card:hover {
  transform: none;
  box-shadow: var(--shadow);
}
.cards.list .card-main {
  display: flex;
  align-items: center;
  flex: 1;
  width: auto;
  min-width: 0;
  gap: 14px;
  padding: 8px 12px 8px 8px;
  border-radius: var(--radius-panel) 0 0 var(--radius-panel);
}
.cards.list .thumb {
  width: 112px;
  height: 70px;
  aspect-ratio: auto;
  flex: none;
  border-radius: var(--radius-panel);
}
.cards.list .meta {
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(88px, 0.7fr) minmax(120px, 0.8fr);
  align-items: center;
  gap: 8px 22px;
  padding: 0 4px;
}
.cards.list .meta strong {
  font-size: 14px;
}
.cards.list .meta .sub {
  display: contents;
}
.cards.list .dot {
  display: none;
}
.cards.list .count,
.cards.list .when {
  font-size: 12.5px;
  color: var(--text-3);
  min-width: 0;
}
.cards.list .when {
  text-align: right;
}
.cards.list .more {
  position: relative;
  top: auto;
  right: auto;
  flex: none;
  align-self: stretch;
  width: 42px;
  height: auto;
  margin: 0;
  opacity: 1;
  border-radius: 0 var(--radius-panel) var(--radius-panel) 0;
  border-left: 1px solid var(--border);
  background: transparent;
  box-shadow: none;
  transform: none;
}
.cards.list .more:hover {
  background: var(--hover);
}
.more {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 30px;
  height: 30px;
  border: none;
  border-radius: var(--radius-control);
  background: var(--float);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.15);
  color: var(--text-2);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.card:hover .more,
.more:focus-visible,
.card:focus-within .more {
  opacity: 1;
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 80px 0;
  color: var(--text-3);
}
.empty-art {
  width: 72px;
  height: 72px;
  border-radius: var(--radius-panel);
  background: var(--panel);
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-3);
}
.cover-block {
  margin: 2px 4px 4px;
  padding: 8px 6px 8px;
  border-radius: var(--radius-panel);
  background: color-mix(in srgb, var(--panel) 92%, transparent);
}
.menu-sec {
  margin: 0 6px 6px;
  font-size: 11px;
  color: var(--text-3);
}
.covers {
  display: grid;
  grid-template-columns: repeat(8, 22px);
  gap: 6px;
  padding: 0 6px;
}
/* menu buttons are full-width rows: swatches keep their round shape */
.menu .covers .swatch-btn {
  width: 22px;
  height: 22px;
  padding: 0;
  border-radius: 999px;
  justify-content: center;
  gap: 0;
}
.covers .swatch-btn.preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--panel-2);
  color: var(--text-3);
}
.covers .swatch-btn.custom {
  background: conic-gradient(#f87171, #facc15, #4ade80, #60a5fa, #c084fc, #f87171);
  overflow: hidden;
}
.covers .swatch-btn.custom input {
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
.menu {
  border-radius: var(--radius-panel);
  visibility: hidden;
}
.menu.ready {
  visibility: visible;
}
.menu.glass button:hover:not(:disabled) {
  background: color-mix(in srgb, var(--text) 8%, transparent);
}
.danger {
  color: var(--danger);
}
.bulk {
  position: fixed;
  z-index: 40;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  max-width: calc(100% - 24px);
}
.bulk .chip.danger {
  color: var(--danger);
}
.bulk .chip:disabled {
  opacity: 0.4;
  cursor: default;
}
@media (max-width: 760px) {
  .cards.list .meta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 0;
  }
  .cards.list .meta .sub {
    display: flex;
  }
  .cards.list .dot {
    display: inline;
  }
  .cards.list .when {
    text-align: left;
  }
  .cards.list .thumb {
    width: 84px;
    height: 52px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .card {
    transition: none;
  }
  .card:hover {
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05), 0 0 0 1px rgba(15, 23, 42, 0.05);
    transform: none;
  }
  .cards.list .card:hover {
    box-shadow: 0 0 0 1px var(--border);
  }
}
</style>
